import React from 'react';
import { Link } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Cell } from 'recharts';
import { getColor } from 'common/constants/_colors';
import { numeralify } from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import noPullRequestsBar from 'images/characters/sunglasses-dudes.svg';
import BarChart from './BarChart';
import { Flex } from './base/layout-components';
import Text from './base/Text';

import ScoreWithDelta from './ScoreWithDelta';
import Icon from './base/Icon';
import { TRACKING } from 'common/constants/constants';

const Chart = styled.div`
  height: 100px;
  width: 100%;
`;

const ControlPointContainer = styled.div`
  margin-top: -2px;
  cursor: pointer;
`;

const RelativeFlex = styled(Flex)`
  position: relative;
`;

const StyledSVG = styled.img`
  width: 15rem;
  height: 15rem;
`;

const onLinkClick = (history, url, name) => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.DASHBOARD,
    page_tab: `Empty State for ${name.replace(/\s/gi, '_')}`,
    click_type: TRACKING.CLICK.SETTINGS
  });
  history.push({
    pathname: url
  });
};

const CustomTooltip = (metricName = '') => {
  return ({ payload = [] }) => {
    const {
      start = '',
      end = '',
      name = '',
      value = 'N/A'
    } = _.get(payload, `[0].payload`, {});
    if (name === '') {
      return null;
    }
    const formattedValue = numeralify(value);

    return (
      <div
        style={{
          backgroundColor: '#fafafa',
          border: '1px solid #3B5998',
          padding: '1rem',
          color: '#3B5998',
          borderRadius: 10,
          width: '15rem'
        }}
      >
        <Text pb='1rem' size='medium' weight='bold' color='chambray'>
          {name.toUpperCase()}
        </Text>
        <Text pb='0.5rem' size='medium'>
          {moment(start).format('MMM D, YYYY')} -{' '}
          {moment(end).format('MMM D, YYYY')}
        </Text>
        <Text size='medium'>
          {formattedValue} {_.capitalize(metricName)}
        </Text>
        {/* <Text size='small' pt='1rem'>{items.map(({ name }) => name).join(', ')}</Text> */}
      </div>
    );
  };
};

const ActivityBox = ({
  name = '',
  value = 0,
  delta = '-',
  pctOfAvg = null,
  avg = 0,
  iterations = [],
  goTo = null,
  history,
  emptyStateLink = null,
  linkText = 'Click Here',
  descriptionText = 'Not as expected?',
  isFinishedOnBoarding,
  dataCyPrefix = ''
}) => {
  const tooltip = CustomTooltip(name.toUpperCase(), '');
  if (
    !_.isEmpty(emptyStateLink) &&
    iterations.length &&
    iterations.every(({ value }) => value === 0 || _.isNull(value)) &&
    isFinishedOnBoarding
  ) {
    return (
      <Flex height='100%' direction='column'>
        <Text
          color='gray'
          size='medium'
          weight='bold'
          pb='1rem'
          lineHeight='1rem'
        >
          {name.toUpperCase()}
        </Text>
        <Text pt='2.6rem' pb='0.5rem' size='medium'>
          {descriptionText}
        </Text>
        <Link onClick={() => onLinkClick(history, emptyStateLink, name)}>
          {linkText}
        </Link>
        <StyledSVG src={noPullRequestsBar} alt='' />
      </Flex>
    );
  }
  return (
    <Flex height='100%' direction='column'>
      <Text
        color='gray'
        size='medium'
        weight='bold'
        pb='1rem'
        lineHeight='1rem'
      >
        {name.toUpperCase()}
      </Text>

      <RelativeFlex direction='row'>
        <ScoreWithDelta
          score={value}
          delta={delta}
          name={name}
          dataCyPrefix={dataCyPrefix}
        />
        {goTo ? (
          <ControlPointContainer
            onClick={() =>
              goTo({
                history
              })
            }
          >
            <Icon name='ControlPoint' size='medium' color='silver' />
          </ControlPointContainer>
        ) : null}
      </RelativeFlex>

      <Text size='medium' pb='1rem'>
        {_.isNumber(pctOfAvg) && !_.isNaN(pctOfAvg)
          ? `${pctOfAvg}% of avg.`
          : '-'}
      </Text>

      <Text size='small' color='gray' pb='1rem'>
        Actual/Prev. iterations
      </Text>

      <Chart>
        <BarChart
          data={iterations}
          config={{
            CustomTooltip: tooltip,
            bars: [
              {
                dataKey: 'value',
                fill: getColor('orangePeel'),
                barSize: 24,
                minPointSize: 2
              }
            ],
            barFn: data => {
              return data.map((entry, index) => (
                <Cell
                  key={`cell_${index.toString()}`}
                  fill={
                    data[index].isSelected
                      ? getColor('chambray')
                      : getColor('chambray', 0.2)
                  }
                />
              ));
            },
            referenceLines:
              avg > 0
                ? [
                    {
                      y: avg,
                      label: {
                        position: 'insideBottomLeft',
                        value: 'Avg.',
                        fill: getColor('orangePeel')
                      },
                      stroke: getColor('orangePeel'),
                      strokeDasharray: '3 3'
                    }
                  ]
                : []
          }}
        />
      </Chart>
    </Flex>
  );
};

ActivityBox.propTypes = {
  name: PropTypes.string,
  value: PropTypes.number,
  delta: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pctOfAvg: PropTypes.number,
  avg: PropTypes.number,
  iterations: PropTypes.array
};

export default ActivityBox;
